import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  Truck,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import { ConfirmDialog } from '@/components/shared/confirm-dialog'
import { VehicleDialog } from '@/components/vehicles/vehicle-dialog'
import { VEHICLE_TYPES, type VehicleRow } from '@/components/vehicles/vehicle-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { type ApiClient, ApiError, type PaginatedResponse } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Vehicles admin page.
 *
 * Same skeleton as Clients (search + table + dialogs +
 * pagination) plus two extra filters: `type` (matches the API's
 * `?type=` filter exactly) and `status` (folded into
 * `?includeInactive=true|false`).
 *
 * The "Hors service" badge maps to `active === false`. Vehicles
 * are NOT soft-deleted here — `DELETE /api/vehicles/:id` is the
 * canonical removal, and inactive is a separate "in maintenance
 * / on hold" semantic. See `vehicles.schema.ts` for the
 * distinction.
 */

const PAGE_SIZE = 20
const QUERY_NS = 'vehicles' as const

type StatusFilter = 'all' | 'active' | 'inactive'

export function VehiclesPage() {
  const api = useApi()
  const queryClient = useQueryClient()

  /* -------- search (debounced) -------- */
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    if (searchDraft === search) return
    const t = setTimeout(() => setSearch(searchDraft), 300)
    return () => clearTimeout(t)
  }, [searchDraft, search])

  /* -------- type + status filters -------- */
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  /* -------- pagination -------- */
  const [page, setPage] = useState(1)
  useEffect(() => {
    setPage(1)
  }, [search, typeFilter, statusFilter])

  /* -------- dialog state -------- */
  const [editing, setEditing] = useState<VehicleRow | null | undefined>(undefined)
  const [pendingDelete, setPendingDelete] = useState<VehicleRow | null>(null)

  /* -------- list query -------- */
  const queryKey = useMemo(
    () =>
      [QUERY_NS, 'list', search, typeFilter, statusFilter, page] as const,
    [search, typeFilter, statusFilter, page],
  )

  const listQuery = useQuery({
    queryKey,
    queryFn: () => fetchVehicles(api, search, typeFilter, statusFilter, page, PAGE_SIZE),
    placeholderData: (prev: PaginatedResponse<VehicleRow> | undefined) => prev,
    staleTime: 30 * 1000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/vehicles/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_NS] })
      setPendingDelete(null)
    },
  })

  const rows = listQuery.data?.data ?? []
  const total = listQuery.data?.meta.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Véhicules</h1>
          <p className="text-sm text-text-muted">Gérez votre parc.</p>
        </div>
        <Button onClick={() => setEditing(null)}>
          <Plus size={16} />
          Nouveau véhicule
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <div className="sm:col-span-6 flex flex-col gap-1.5">
              <Label htmlFor="vehicles-search">Recherche</Label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
                  aria-hidden="true"
                />
                <Input
                  id="vehicles-search"
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Immatriculation, marque, modèle…"
                  className="pl-9"
                />
                {searchDraft !== '' ? (
                  <button
                    type="button"
                    aria-label="Effacer"
                    onClick={() => {
                      setSearchDraft('')
                      setSearch('')
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-text-muted hover:text-text hover:bg-surface-hover"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="sm:col-span-3 flex flex-col gap-1.5">
              <Label htmlFor="vehicles-type">Type</Label>
              <select
                id="vehicles-type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className={cn(
                  'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
                  'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
                )}
              >
                <option value="">Tous les types</option>
                {VEHICLE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-3 flex flex-col gap-1.5">
              <Label htmlFor="vehicles-status">Statut</Label>
              <select
                id="vehicles-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className={cn(
                  'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
                  'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
                )}
              >
                <option value="all">Tous</option>
                <option value="active">Actifs</option>
                <option value="inactive">Hors service</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="py-16 flex justify-center">
            <Spinner size={28} label="Chargement des véhicules" />
          </div>
        ) : listQuery.isError ? (
          <div className="px-6 py-12 text-center text-danger">
            Impossible de charger les véhicules.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-text-muted">
            <Truck size={28} className="mx-auto mb-2 opacity-60" aria-hidden="true" />
            <p>Aucun véhicule ne correspond aux filtres.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-text-muted">
                <tr className="text-left">
                  <Th>Immatriculation</Th>
                  <Th>Type</Th>
                  <Th>Marque / Modèle</Th>
                  <Th className="text-right">Kilométrage</Th>
                  <Th className="text-center w-32">Statut</Th>
                  <Th className="w-12" srOnly>
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <VehicleRow
                    key={row.id}
                    row={row}
                    onEdit={() => setEditing(row)}
                    onDelete={() => setPendingDelete(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > 0 ? (
        <div className="mt-4 flex items-center justify-between text-sm text-text-muted">
          <p>
            Affichage {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || listQuery.isFetching}
            >
              <ChevronLeft size={14} />
              Précédent
            </Button>
            <span className="px-2 text-text">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || listQuery.isFetching}
            >
              Suivant
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      ) : null}

      <VehicleDialog
        vehicle={editing}
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined)
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null)
            deleteMutation.reset()
          }
        }}
        title="Supprimer le véhicule ?"
        description="Le véhicule disparaîtra des sélecteurs des plannings et missions. Les missions historiques restent intactes."
        confirmLabel="Supprimer"
        tone="danger"
        loading={deleteMutation.isPending}
        errorMessage={
          deleteMutation.error instanceof ApiError
            ? deleteMutation.error.message
            : null
        }
        onConfirm={() => {
          if (pendingDelete !== null) deleteMutation.mutate(pendingDelete.id)
        }}
      >
        {pendingDelete !== null ? (
          <div className="text-sm text-text">
            <p className="font-medium">{pendingDelete.registration}</p>
            <p className="text-text-muted text-xs mt-1">
              {[pendingDelete.brand, pendingDelete.model, pendingDelete.type]
                .filter((s) => s !== null && s !== '')
                .join(' · ') || '—'}
            </p>
          </div>
        ) : null}
      </ConfirmDialog>
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Row                                                                        */
/* -------------------------------------------------------------------------- */

interface VehicleRowProps {
  row: VehicleRow
  onEdit: () => void
  onDelete: () => void
}

function VehicleRow({ row, onEdit, onDelete }: VehicleRowProps) {
  const brandModel = [row.brand, row.model]
    .filter((s) => s !== null && s !== '')
    .join(' ')
  const mileage = row.currentMileage ?? row.initialMileage
  return (
    <tr className="border-t border-surface-border hover:bg-surface-hover transition-colors">
      <Td className="font-mono font-medium text-text uppercase">
        {row.registration}
      </Td>
      <Td className="text-text-muted">
        {row.type !== null && row.type !== '' ? row.type : '—'}
      </Td>
      <Td className="text-text-muted">{brandModel || '—'}</Td>
      <Td className="text-right font-mono tabular-nums text-text-muted">
        {mileage > 0 ? `${formatKm(mileage)} km` : '—'}
      </Td>
      <Td className="text-center">
        {row.active ? (
          <Badge tone="success">Actif</Badge>
        ) : (
          <Badge tone="danger">Hors service</Badge>
        )}
      </Td>
      <Td className="w-12 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions pour ${row.registration}`}
              className="p-1.5 rounded-md text-text-muted hover:bg-surface-hover hover:text-text"
            >
              <MoreVertical size={16} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onEdit()}>
              <Pencil size={14} aria-hidden="true" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem danger onSelect={() => onDelete()}>
              <Trash2 size={14} aria-hidden="true" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Td>
    </tr>
  )
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function Th({
  className,
  children,
  srOnly = false,
}: {
  className?: string
  children: React.ReactNode
  srOnly?: boolean
}) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 font-medium uppercase tracking-wide text-xs',
        className,
      )}
    >
      {srOnly ? <span className="sr-only">{children}</span> : children}
    </th>
  )
}

function Td({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <td className={cn('px-3 py-2.5', className)}>{children}</td>
}

function formatKm(km: number): string {
  // French thousands separator (non-breaking space).
  return km.toLocaleString('fr-FR')
}

async function fetchVehicles(
  api: ApiClient,
  search: string,
  typeFilter: string,
  statusFilter: StatusFilter,
  page: number,
  limit: number,
): Promise<PaginatedResponse<VehicleRow>> {
  const params: Record<string, string | number | boolean> = {
    page,
    limit,
    sortBy: 'registration',
    sortOrder: 'asc',
    // The API filter is `includeInactive=true` for "show both";
    // omitted means active-only. Inactive-only requires a
    // post-filter on the client (small dataset → cheap).
    includeInactive: statusFilter !== 'active',
  }
  if (search.trim() !== '') params.search = search.trim()
  if (typeFilter !== '') params.type = typeFilter
  const result = await api.getPaginated<VehicleRow>('/api/vehicles', params)
  // Inactive-only filter: post-filter (max 200 rows from the
  // API page, no perceptible cost). This keeps the API surface
  // unchanged.
  if (statusFilter === 'inactive') {
    return {
      ...result,
      data: result.data.filter((v) => !v.active),
      // `meta.total` becomes wrong in this branch; for the MVP
      // we accept the discrepancy. A dedicated server-side
      // `?active=false` filter would be the clean fix.
      meta: { ...result.meta, total: result.data.filter((v) => !v.active).length },
    }
  }
  return result
}
